import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { portalFetch } from '../lib/supabase';

function Card({ children, style }) {
  return (
    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, ...style }}>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid:       { label: 'Paid',       bg: '#D1FAE5', color: '#065F46' },
    processing: { label: 'Processing', bg: '#DBEAFE', color: '#1E40AF' },
    pending:    { label: 'Pending',    bg: '#FEF3C7', color: '#92400E' },
    failed:     { label: 'Failed',     bg: '#FEE2E2', color: '#991B1B' },
  };
  const cfg = map[status] || map.pending;
  return (
    <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 10, background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

const fmt     = (n) => `$${parseFloat(n || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric'
}) : '–';

function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : '–'; }

export default function Settlements() {
  const { restaurant } = useAuth();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    if (!restaurant?.id) return;
    setLoading(true);
    const result = await portalFetch('/settlements');
    if (result) setData(result);
    setLoading(false);
  }, [restaurant?.id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>Loading settlements...</div>
  );

  const { settlements = [], billing = {}, summary = {} } = data || {};
  const payoutMethod    = billing.payout_method || null;
  const isStripeConnect = payoutMethod === 'stripe_connect';
  const isBankTransfer  = payoutMethod === 'bank_transfer';
  const isNotSetUp      = !payoutMethod;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900, margin: 0 }}>Settlements</h1>
        <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginTop: 4 }}>
          Weekly payouts — every Monday for the prior Mon–Sun period
        </p>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total paid out',        value: fmt(summary.totalPaidAllTime),                color: 'var(--orange)' },
          { label: 'Settlements',           value: summary.settlementCount ?? '–',               color: undefined },
          { label: 'Order processing fee',  value: `${billing.processing_fee_pct ?? 12}%`,       color: undefined },
          { label: 'Current tier',          value: capitalize(billing.service_tier || 'starter'), color: undefined },
        ].map(({ label, value, color }) => (
          <Card key={label} style={{ padding: '1.2rem 1.4rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.4rem' }}>
              {label}
            </div>
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: '1.6rem', fontWeight: 900, color: color || 'var(--dark)' }}>
              {value}
            </div>
          </Card>
        ))}
      </div>

      {/* Payout method info */}
      <Card style={{ padding: '1rem 1.4rem', marginBottom: '1.5rem', background: isStripeConnect ? '#EEF2FF' : (isBankTransfer ? '#F0FDF4' : 'var(--warm)'), border: `1px solid ${isStripeConnect ? '#C7D2FE' : (isBankTransfer ? '#BBF7D0' : 'var(--border)')}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.2rem' }}>{isStripeConnect ? '💳' : (isBankTransfer ? '🏦' : '⚠️')}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--dark)' }}>
                {isStripeConnect ? 'Stripe Connect' : (isBankTransfer ? 'TableChat Managed Payments' : 'Payout method not set up')}
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: 2 }}>
                {isStripeConnect
                  ? `Stripe account ${billing.stripe_connect_account_id || '–'} · Direct payouts`
                  : isBankTransfer
                    ? (billing.bank_account_last4
                        ? `Bank account ending ····${billing.bank_account_last4} · Routing ····${billing.bank_routing_last4}`
                        : 'No bank account on file — contact support to set up payouts')
                    : 'Set up payouts in the Payouts tab to start receiving weekly settlements'}
              </div>
            </div>
          </div>
          <span style={{
            fontSize: '0.72rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 10,
            background: billing.payout_status === 'active' ? '#D1FAE5' : '#FEF3C7',
            color: billing.payout_status === 'active' ? '#065F46' : '#92400E',
          }}>
            {billing.payout_status === 'active' ? 'Active' : 'Pending Setup'}
          </span>
        </div>
      </Card>

      {/* Settlements table */}
      {settlements.length === 0 ? (
        <Card>
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.4 }}>💰</div>
            <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
              No settlements yet. Your first payout will appear here after your first week of orders.
            </p>
          </div>
        </Card>
      ) : (
        <Card>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 100px 28px', gap: '0.75rem', padding: '0.75rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--warm)', borderRadius: '12px 12px 0 0' }}>
            {['Period', 'Gross', 'Fees', 'Net Payout', 'Orders', 'Status', ''].map(h => (
              <div key={h} style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
            ))}
          </div>

          {settlements.map(s => {
            const isExp      = expanded === s.id;
            const totalFees  = parseFloat(s.stripe_fees || 0) + parseFloat(s.processing_fees || 0) + parseFloat(s.reservation_fees || 0) + parseFloat(s.uber_delivery_fees || 0);
            const resFee     = parseFloat(billing.reservation_fee || 1.50).toFixed(2);

            return (
              <div key={s.id}>
                <div
                  onClick={() => setExpanded(isExp ? null : s.id)}
                  style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 100px 28px', gap: '0.75rem', padding: '1rem 1.4rem', borderBottom: '1px solid var(--warm)', alignItems: 'center', fontSize: '0.875rem', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>{fmtDate(s.week_start)}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>to {fmtDate(s.week_end)}</div>
                  </div>
                  <div style={{ fontWeight: 600 }}>{fmt(s.gross_revenue)}</div>
                  <div style={{ color: '#DC2626' }}>−{fmt(totalFees)}</div>
                  <div style={{ fontWeight: 700, color: 'var(--orange)' }}>{fmt(s.net_payout)}</div>
                  <div style={{ color: 'var(--mid)' }}>{s.order_count}</div>
                  <div><StatusBadge status={s.status} /></div>
                  <div style={{ color: 'var(--muted)', fontSize: '0.85rem', textAlign: 'center' }}>{isExp ? '▲' : '▾'}</div>
                </div>

                {/* Expanded breakdown */}
                {isExp && (
                  <div style={{ background: 'var(--cream)', borderBottom: '1px solid var(--border)', padding: '1.2rem 1.4rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                      {/* Fee breakdown */}
                      <div>
                        <p style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
                          Settlement breakdown
                        </p>
                        {[
                          { label: 'Gross Revenue',                        value: fmt(s.gross_revenue),                      color: 'var(--dark)' },
                          { label: 'Stripe Processing Fee',                value: `−${fmt(s.stripe_fees)}`,                  color: '#DC2626' },
                          { label: `TableChat Order Processing Fee (${billing.processing_fee_pct ?? 12}%)`, value: `−${fmt(s.processing_fees)}`, color: '#DC2626' },
                          ...(s.reservation_count > 0 ? [{ label: `Reservation Fee (${s.reservation_count} × $${resFee})`, value: `−${fmt(s.reservation_fees)}`, color: '#DC2626' }] : []),
                          ...(parseFloat(s.uber_delivery_fees || 0) > 0 ? [{ label: `TableChat Delivery Fees (${s.uber_delivery_count || 0} deliveries)`, value: `−${fmt(s.uber_delivery_fees)}`, color: '#DC2626' }] : []),
                          ...(parseFloat(s.own_driver_delivery_fees || 0) > 0 ? [{ label: 'Own-Driver Delivery Fees (included in payout)', value: fmt(s.own_driver_delivery_fees), color: '#0F6E56' }] : []),
                          { label: 'Tax Collected (passed through)',       value: fmt(s.tax_collected),                      color: 'var(--muted)' },
                          { label: 'Net Payout',                           value: fmt(s.net_payout),                         color: 'var(--orange)', bold: true },
                        ].map(({ label, value, color, bold }) => (
                          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.875rem' }}>
                            <span style={{ color: 'var(--mid)' }}>{label}</span>
                            <span style={{ color, fontWeight: bold ? 700 : 400 }}>{value}</span>
                          </div>
                        ))}
                      </div>

                      {/* Payout details */}
                      <div>
                        <p style={{ fontWeight: 700, fontSize: '0.8rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
                          Payout details
                        </p>
                        <div style={{ fontSize: '0.875rem', color: 'var(--mid)', lineHeight: 2 }}>
                          <div>📅 Period: {fmtDate(s.week_start)} – {fmtDate(s.week_end)}</div>
                          <div>🛍️ Orders: {s.order_count}</div>
                          {s.reservation_count > 0 && <div>📅 Reservations: {s.reservation_count}</div>}
                          <div>
                            {isStripeConnect
                              ? `💳 To: Stripe account ${billing.stripe_connect_account_id || '–'}`
                              : `🏦 To: Bank ····${billing.bank_account_last4 || '–'}`}
                          </div>
                          {s.stripe_transfer_id && (
                            <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: 4 }}>
                              Transfer ID: {s.stripe_transfer_id}
                            </div>
                          )}
                          {s.payout_initiated_at && (
                            <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
                              Initiated: {new Date(s.payout_initiated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </div>
                          )}
                        </div>
                        <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, fontSize: '0.75rem', color: '#92400E' }}>
                          💡 Tax collected ({fmt(s.tax_collected)}) is passed through to you in full.
                          You are responsible for remitting to the relevant tax authority.
                          Own-driver delivery fees are yours entirely — no TableChat commission.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}
